import {
  Button,
  CommandDialog,
  type CommandDialogItem,
  AppShell as Shell,
  type ShellNavGroup,
  useTranslation,
} from '@hbs-us/ui';
import logoUrl from '@hbs-us/ui/assets/logo.svg';
// The lockup ships in two inks and the file names do NOT say which surface each
// one is for, which is how the cream wordmark ended up on the light sidebar.
// Named by the surface they sit on: `qelm-lockup.svg` is the cream (#F4F0E8)
// wordmark for dark surfaces, `qelm-lockup-light.svg` the ink (#161616) one for
// light. The orange mark is identical in both.
import lockupOnDarkUrl from '@hbs-us/ui/assets/qelm-lockup.svg';
import lockupOnLightUrl from '@hbs-us/ui/assets/qelm-lockup-light.svg';
import { Link, Outlet, useLocation, useNavigate } from '@tanstack/react-router';
import {
  ChevronDown,
  Home,
  Languages,
  LifeBuoy,
  LogOut,
  Plus,
  Search,
  Settings,
  UserCog,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { useAccess } from '../auth/useAccess.js';
import { useFeatureAccess, useFeatureFlag } from '../auth/useFeatureAccess.js';
import { useSession } from '../auth/useSession.js';
import { analyticsMaskClassForRoute, capturePageView, routeIdForPath } from './analytics.js';
import { BlockedRouteNotice } from './BlockedRouteNotice.js';
import { ImpersonationBanner } from './components/ImpersonationBanner.js';
import { NotificationBell } from './components/NotificationBell.js';
import { StatusBanner } from './components/StatusBanner.js';
import { LanguageSwitcher } from './LanguageSwitcher.js';
import { HOME_ITEM, NAV_GROUPS, PALETTE_DESTINATIONS } from './navData.js';
import { PlatformShell } from './shell/PlatformShell.js';

/**
 * Durable app-shell chrome (D-01, D-04). P25 SHELL-1: the bespoke sidebar +
 * top bar are replaced by the shared `@hbs-us/ui` AppShell composite (the
 * Intuit-style left rail + rich top bar, light-led, orange active pill), reused
 * by the platform-admin app in a later phase. This file now only OWNS the tenant
 * config — the six nav sections, the brand lockup, the impersonation banner, and
 * the top-bar slots (identity · New job · bell · language · sign-out).
 *
 * The nav destinations themselves (paths, icons, labels, access rules) live in
 * `navData.ts` — ONE list shared with the platform shell (see shell/), so both
 * chrome variants render the same guarded destinations. This file is the
 * flag-OFF shell: `new_shell` (see PlatformShell) swaps the sidebar for the
 * platform top bar; the sidebar code stays until that rollout completes.
 *
 * Non-regression: every navigation destination and top-bar function the prior
 * shell had is preserved; every color stays a token-backed utility (zero
 * hardcoded hex — BRAND-06), and analytics masking + page-view capture are
 * unchanged.
 */

/**
 * Translate the local nav consts into the shared shell's grouped nav shape, dropping
 * every destination the signed-in user cannot reach. A group whose items are all
 * filtered away is dropped too, so nobody gets an empty accordion header.
 *
 * This is presentation only. The api worker re-checks all of it (see `useAccess`).
 */
function useShellNav(): ShellNavGroup[] {
  const { t } = useTranslation();
  const canAccess = useAccess();
  const canSeeFeature = useFeatureAccess();

  const groups = NAV_GROUPS.map((group) => ({
    label: t(group.labelKey),
    icon: (() => {
      const Icon = group.icon;
      return <Icon aria-hidden="true" />;
    })(),
    defaultOpen:
      group.labelKey === 'nav:groups.operations' || group.labelKey === 'nav:groups.finances',
    items: group.items
      .filter((item) => canSeeFeature(item.to))
      .filter((item) => canAccess(item.access))
      .map((item) => {
        const Icon = item.icon;
        return { to: item.to, label: t(item.labelKey), icon: <Icon aria-hidden="true" /> };
      }),
  })).filter((group) => group.items.length > 0);

  return [
    {
      items: canAccess(HOME_ITEM.access)
        ? [
            {
              to: HOME_ITEM.to,
              label: t(HOME_ITEM.labelKey),
              icon: <Home aria-hidden="true" />,
              exact: true,
            },
          ]
        : [],
    },
    ...groups,
  ].filter((group) => group.items.length > 0);
}

/** Left identity block for the top bar — tenant name over the signed-in user. */
function TopBarIdentity() {
  const { session } = useSession();
  return (
    <>
      <span className="block truncate font-semibold text-foreground text-sm">
        {session?.tenant.name ?? ''}
      </span>
      <span className="block truncate text-muted-foreground text-xs">
        {session?.user.name ?? ''}
      </span>
    </>
  );
}

const FOOTER_LINK_CLASS =
  'flex h-9 w-full items-center gap-2.5 rounded-md px-2.5 text-[13px] font-medium text-muted-foreground ' +
  'transition-[color,background-color,transform] duration-[var(--dur-quick)] ease-[var(--ease-out)] ' +
  'hover:bg-secondary hover:text-foreground active:scale-[0.96] motion-reduce:transition-none ' +
  'motion-reduce:active:scale-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ' +
  'focus-visible:ring-offset-2 focus-visible:ring-offset-sidebar group-data-[collapsed=true]/sidebar:justify-center ' +
  'group-data-[collapsed=true]/sidebar:px-0';

function ProfileMenu() {
  const { t } = useTranslation();
  const { session, signOut } = useSession();
  const initial = session?.user.name.trim().charAt(0).toUpperCase() || '?';

  return (
    <details className="group/profile relative">
      <summary
        aria-label={t('nav:profile.openMenu')}
        className={`${FOOTER_LINK_CLASS} list-none cursor-pointer [&::-webkit-details-marker]:hidden`}
      >
        <span className="flex size-7 shrink-0 items-center justify-center rounded-md bg-primary/10 font-semibold text-primary-ink text-xs">
          {initial}
        </span>
        <span className="min-w-0 flex-1 group-data-[collapsed=true]/sidebar:sr-only">
          <span className="block truncate text-foreground">{session?.user.name ?? ''}</span>
          <span className="block truncate text-[11px] text-muted-foreground">
            {session?.user.email ?? ''}
          </span>
        </span>
        <ChevronDown
          className="size-3.5 transition-transform group-open/profile:rotate-180 group-data-[collapsed=true]/sidebar:hidden"
          aria-hidden="true"
        />
      </summary>
      <div className="absolute bottom-full left-0 z-50 mb-2 w-56 rounded-lg border border-border bg-popover p-1.5 text-popover-foreground shadow-lg group-data-[collapsed=true]/sidebar:left-12">
        <Link to="/account" className={FOOTER_LINK_CLASS}>
          <UserCog className="size-4" aria-hidden="true" />
          <span>{t('nav:items.userSettings')}</span>
        </Link>
        <div className="flex items-center justify-between gap-2 rounded-md px-2.5 py-1.5 md:hidden">
          <span className="flex items-center gap-2.5 text-foreground text-sm">
            <Languages className="size-4" aria-hidden="true" />
            {t('nav:language.label')}
          </span>
          <LanguageSwitcher />
        </div>
        <button type="button" className={FOOTER_LINK_CLASS} onClick={() => void signOut()}>
          <LogOut className="size-4" aria-hidden="true" />
          <span>{t('nav:topbar.signOut')}</span>
        </button>
      </div>
    </details>
  );
}

function SidebarFooterContent() {
  const { t } = useTranslation();
  const canAccess = useAccess();
  return (
    <div className="flex w-full flex-col gap-1">
      {/* Support and the profile menu stay open to everyone: needing help, and reaching
          your own account, are not privileges. Company settings is a real destination
          (`GET /api/v1/settings` -> settings.view), so it is gated like one. */}
      <Link to="/support" className={FOOTER_LINK_CLASS}>
        <LifeBuoy className="size-4" aria-hidden="true" />
        <span className="group-data-[collapsed=true]/sidebar:sr-only">
          {t('nav:items.qelmSupport')}
        </span>
      </Link>
      {canAccess({ permission: 'settings.view' }) ? (
        <Link to="/settings" className={FOOTER_LINK_CLASS}>
          <Settings className="size-4" aria-hidden="true" />
          <span className="group-data-[collapsed=true]/sidebar:sr-only">
            {t('nav:items.companySettings')}
          </span>
        </Link>
      ) : null}
      <ProfileMenu />
    </div>
  );
}

/** Trailing top-bar cluster: support · notifications · language. */
function TopBarActions() {
  const { t } = useTranslation();
  return (
    <>
      <Button variant="ghost" asChild className="min-h-11 min-w-11">
        {/* Below xl the word is hidden, and an icon-only link has no name to read out. */}
        <Link to="/support" aria-label={t('nav:items.qelmSupport')}>
          <LifeBuoy aria-hidden="true" />
          <span className="hidden xl:inline">{t('nav:items.qelmSupport')}</span>
        </Link>
      </Button>
      <NotificationBell />
      <div className="hidden md:block">
        <LanguageSwitcher />
      </div>
    </>
  );
}

/**
 * The cmd-K palette. Its entries are DERIVED from `navData.ts` — every destination that declares
 * a `palette` field — and pass through the SAME two gates the sidebar applies, via the same
 * helpers: `useAccess` for the user's rule and `useFeatureAccess` for the tenant's.
 *
 * That derivation is the point. The palette used to hand-write ten entries and re-type each
 * one's access rule beside it, with nothing enforcing that it matched the nav item it jumped to.
 * A palette entry whose rule drifts is a back door around the hidden sidebar: cmd-K, type
 * "Payroll", land on a 403. Hiding is UX, never authorization — the api worker re-checks all of
 * it — but a destination the user cannot reach has no business being offered.
 */
export function GlobalSearch() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const canAccess = useAccess();
  const canSeeFeature = useFeatureAccess();
  const [open, setOpen] = useState(false);

  const items: CommandDialogItem[] = PALETTE_DESTINATIONS.filter((destination) =>
    canSeeFeature(destination.to),
  )
    .filter((destination) => canAccess(destination.access))
    .map((destination) => {
      const Icon = destination.icon;
      return {
        id: destination.id,
        label: t(destination.labelKey),
        description: t(destination.palette.descriptionKey),
        keywords: destination.palette.keywords,
        icon: <Icon />,
        // `to` comes from the ONE list of real routes, and every list route's `validateSearch`
        // fills its own defaults from an empty record — so jumping by path alone lands on the
        // same URL the hand-written entries built by hand. The cast is the trade the platform
        // shell's palette already makes (see shell/SearchPanel.tsx) for a path typed as a string.
        onSelect: () => void navigate({ to: destination.to } as never),
      };
    });

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setOpen(true);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="hidden h-10 w-[17rem] items-center xl:w-auto xl:min-w-[17rem] xl:max-w-[24rem] gap-2 rounded-full border border-border bg-background px-3 text-muted-foreground text-sm transition-[color,background-color,transform] duration-[var(--dur-quick)] ease-[var(--ease-out)] hover:bg-secondary active:scale-[0.96] motion-reduce:transition-none motion-reduce:active:scale-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background lg:flex"
      >
        <Search className="size-4" aria-hidden="true" />
        <span className="min-w-0 flex-1 truncate text-left">{t('nav:search.placeholder')}</span>
        <kbd className="rounded border border-border bg-card px-1.5 py-0.5 font-mono text-2xs">
          ⌘K
        </kbd>
      </button>
      <CommandDialog
        open={open}
        onOpenChange={setOpen}
        title={t('nav:search.title')}
        placeholder={t('nav:search.placeholder')}
        emptyLabel={t('nav:search.empty')}
        items={items}
      />
    </>
  );
}

/**
 * The flag-OFF sidebar shell — the shared kit AppShell composite with the tenant
 * config this file owns: grouped nav, brand lockup, sidebar footer, and the
 * top-bar slots (identity · search · New job · bell · language · support).
 */
function SidebarShell() {
  const { t } = useTranslation();
  const { session } = useSession();
  const location = useLocation();
  const routeId = routeIdForPath(location.pathname);
  const nav = useShellNav();
  const canAccess = useAccess();

  useEffect(() => {
    capturePageView(routeId);
  }, [routeId]);

  return (
    <Shell
      brand={
        // Both inks stay in the DOM and `.dark` picks the readable one, so the
        // swap costs no JS and cannot flash the wrong wordmark on first paint.
        // The name lives on the wrapper so exactly one is announced either way.
        <span role="img" aria-label={t('nav:appName')} className="inline-flex">
          <img src={lockupOnLightUrl} alt="" className="h-7 w-auto [.dark_&]:hidden" />
          <img src={lockupOnDarkUrl} alt="" className="hidden h-7 w-auto [.dark_&]:block" />
        </span>
      }
      compactBrand={<img src={logoUrl} alt="" className="size-7 object-contain" />}
      workspace={{
        mark: session?.tenant.name.trim().charAt(0).toUpperCase() || '?',
        name: session?.tenant.name ?? '',
        meta: t('nav:workspace.label'),
      }}
      nav={nav}
      navLabel={t('nav:appName')}
      sidebarFooter={<SidebarFooterContent />}
      banner={<ImpersonationBanner />}
      topBar={{
        title: <TopBarIdentity />,
        search: <GlobalSearch />,
        // `POST /api/v1/jobs` is guarded by the `jobs.create` key, so the button is too.
        actions: canAccess({ permission: 'jobs.create' }) ? (
          <Button variant="secondary" asChild className="min-h-11 min-w-11 px-3 sm:px-4">
            <Link to="/jobs/new" aria-label={t('nav:topbar.newJob')}>
              <Plus aria-hidden="true" />
              <span className="hidden sm:inline">{t('nav:topbar.newJob')}</span>
            </Link>
          </Button>
        ) : undefined,
        right: <TopBarActions />,
      }}
      belowTopBar={<StatusBanner />}
      mainClassName={analyticsMaskClassForRoute(routeId)}
    >
      <BlockedRouteNotice />
      <Outlet />
    </Shell>
  );
}

/**
 * The router's root component, and the rollout switch: `new_shell` (a tenant
 * feature, read off the session) decides WHICH chrome wraps the same routes.
 * Flag off renders the sidebar shell above, byte-for-byte today's chrome; flag
 * on renders the platform shell (top bar + 9-dot launcher + cmd-K). Both shells
 * read the ONE shared nav list, so no destination or access rule can differ
 * between them. The sidebar code stays until the rollout completes, then goes.
 */
export function AppShell() {
  const usePlatformShell = useFeatureFlag('new_shell');
  if (usePlatformShell) return <PlatformShell />;
  return <SidebarShell />;
}
